import { forwardRef } from "react";
import {
  Platform,
  StyleSheet,
  TextInput,
  type TextInputProps,
} from "react-native";

import type { UiTheme } from "../theme";

// THE shared text input. First generic primitive in @jackedlogs/ui, born to
// end the 40-file TextInput silo (next/consolidation.md) — one box, one fix,
// one place to change. Encodes the real Android-inflation fix ONCE:
// `minHeight` (not a hard height, so the box grows under font-scale instead of
// clipping — the myopic-not-low-vision rule) + `paddingVertical: 0` on the
// single-line box, which otherwise gains a large native vertical pad on Android
// and looks giant (Antenore 2026-07-02). Multiline legitimately needs vertical
// padding, so that mode restores it and pins text to the top.
//
// It is a LEAF (returns the bare TextInput) so it slots into the ~40 diverse
// layouts as a drop-in — the caller owns width/flex/margins via `style` (merged
// last). a11y CONTRACT for `invalid`: on web it sets `aria-invalid`; React
// Native has no equivalent invalid a11y state, so the field alone would be a
// red border and nothing else. `invalid` must therefore ALWAYS be paired with a
// caller-rendered, visible error line beside the field — that is the non-colour
// cue (the reduced-vision floor). The primitive cannot enforce it; the caller
// owns it. Numeric entry folds in via `numeric` (retires the separately-planned
// <NumericField>): decimal-pad + tabular-nums + centre.

export type TextFieldProps = Omit<TextInputProps, "style"> & {
  theme: UiTheme;
  /** Text alignment. Defaults to `center` when `numeric`, else `left`. */
  align?: "left" | "center" | "right";
  /** Growable multi-line box (notes, feedback). Restores vertical padding. */
  multiline?: boolean;
  /** Multiline min height, in text rows (default 2). Ignored single-line. */
  minRows?: number;
  /** Error state — pair with a caller-rendered error line (never colour-alone). */
  invalid?: boolean;
  /** Decimal numeric entry: decimal-pad keyboard + tabular figures + centre. */
  numeric?: boolean;
  /** Layout-only override (width/flex/margins). Merged last; box internals stay. */
  style?: TextInputProps["style"];
};

export const TextField = forwardRef<TextInput, TextFieldProps>(function TextField(
  {
    theme,
    align,
    multiline = false,
    minRows = 2,
    invalid = false,
    numeric = false,
    style,
    placeholderTextColor,
    keyboardType,
    ...rest
  },
  ref,
) {
  const styles = makeStyles(theme);
  const effectiveAlign = align ?? (numeric ? "center" : "left");

  return (
    <TextInput
      ref={ref}
      multiline={multiline}
      keyboardType={keyboardType ?? (numeric ? "decimal-pad" : undefined)}
      placeholderTextColor={placeholderTextColor ?? theme.palette.ink3}
      accessibilityState={{ disabled: rest.editable === false }}
      // Non-visual invalid cue so `invalid` is never colour-alone (the red
      // border pairs with a caller-rendered error line for sighted users).
      {...(invalid && Platform.OS === "web" ? { "aria-invalid": true } : {})}
      style={[
        styles.base,
        multiline
          ? [styles.multiline, { minHeight: rowHeight(theme) * minRows + theme.spacing.s3 }]
          : styles.singleLine,
        { textAlign: effectiveAlign },
        numeric && styles.numeric,
        invalid && styles.invalid,
        style,
      ]}
      {...rest}
    />
  );
});

// One text row in px at the base size — token-derived, so multiline heights
// track the type scale instead of a magic number.
// ⭐ EXPORTED because a caller that caps this box's height has to count rows in
// the SAME unit the box grows in. PrescribeHeaderFields re-derived this formula
// verbatim first (review finding); two copies of one arithmetic diverge the day
// the type scale moves, and the symptom would be a cap that no longer lands on
// a whole line.
export function rowHeight(theme: UiTheme): number {
  return Math.round(theme.typography.sizes.base * theme.typography.leading.base);
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    base: {
      paddingHorizontal: s.s3,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      color: p.ink,
      fontSize: t.sizes.base,
    },
    // minHeight (not height): the box grows under font-scale, never clips.
    // paddingVertical 0: kills Android's native vertical inflation.
    singleLine: { minHeight: 36, paddingVertical: 0 },
    multiline: { paddingVertical: s.s2, textAlignVertical: "top" },
    numeric: { fontVariant: ["tabular-nums"] },
    invalid: { borderColor: p.danger },
  });
}
